import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, ChevronLeft, ChevronRight, ArrowUpDown, RefreshCw, 
  MapPin, Download, X, Globe, Building2 
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from './axiosConfig'; 
import { useFontSize } from './FontSizeContext';
import { useTranslation } from 'react-i18next'; 
import { usePermissions } from './usePermissions';

const LocationList = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const isSuperAdmin = session.role?.name === 'SUPER_ADMIN';
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [showAllSites, setShowAllSites] = useState(false);
  const [orgsList, setOrgsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);

  const [filters, setFilters] = useState({
    location_id: "",
    description: "",
    type: "",
    status: "",
    site_id: ""
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'location_id', direction: 'asc' });
  const itemsPerPage = 20;
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;
  const { canCreate, canUpdate, canDelete } = usePermissions('locations');

  const fetchLocations = useCallback(async (isAll = showAllSites) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const forceAllSites = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
      const effectiveAllSites = isAll || forceAllSites;

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        q: searchTerm,
        all_sites: isSuperAdmin ? Boolean(effectiveAllSites) : false,
        location_id: filters.location_id,
        description: filters.description,
        type: filters.type,
        status: filters.status,
        site_id: effectiveAllSites ? (selectedSite || filters.site_id) : sessionSite,
        org_id: effectiveAllSites ? (selectedOrg || filters.org_id) : sessionOrg,
        sort_by: sortConfig.key,       
        order: sortConfig.direction    
      };
      Object.keys(params).forEach(key => (params[key] === "" || params[key] === undefined) && delete params[key]);
      const res = await api.get('/api/locations', { headers, params });
      
      setLocations(res.data.data || []);
      setTotalRecords(res.data.total || 0);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error(t('locationList.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filters, sortConfig, t, showAllSites, sessionSite, sessionOrg, isSuperAdmin, selectedOrg, selectedSite]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const fetchInitialData = useCallback(async () => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    try {
      // تم توحيد الاستدعاء باستخدام api لتفادي مشاكل axios
      const orgsRes = await api.get('/api/organizations', { headers });
      setOrgsList(orgsRes.data);
    } catch (err) { console.error("Initial Load Error:", err); }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    if (selectedOrg) {
      const org = orgsList.find(o => o.org_id === selectedOrg);
      setSitesList(org ? org.sites : []);
    } else {
      setSitesList([]);
    }
    setSelectedSite(""); 
    setCurrentPage(1); 
  }, [selectedOrg, orgsList]);

  const handleRowClick = (id) => {
    // 1. تجميع كل الفلاتر النشطة وتنقيتها من القيم الفاضية
    const activeFilters = Object.fromEntries(
      Object.entries({ 
        ...filters, 
        searchParams: searchTerm, // 🎯 غيرنا الاسم ليطابق الباك إند
        all_sites: isSuperAdmin ? Boolean(showAllSites) : false, // تأكد من تمرير القيمة الصحيحة
        site_id: selectedSite || filters.site_id || (showAllSites ? '' : sessionSite) // تمرير السايت بدقة
      }).filter(([_, value]) => value !== '' && value !== null && value !== undefined)
    );
  
    // 2. تحويل الأوبجيكت لـ Query String (مثال: ?status=OPERATING&searchParams=ROOM)
    const queryParams = new URLSearchParams(activeFilters).toString();
  
    // 3. التوجيه للشاشة مع دمج الـ Query
    navigate(`/locations/${id}${queryParams ? '?' + queryParams : ''}`);
  };

  const requestSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({ location_id: "", description: "", type: "", status: "", site_id: "" });
    setSearchTerm("");
    setSortConfig({ key: 'location_id', direction: 'asc' });
    setCurrentPage(1);
  };

  useEffect(() => {
      const openAllSites = () => { 
          if (isSuperAdmin) {
              setShowAllSites(true); 
              setCurrentPage(1); 
          }
      };
      const viewMySite = () => { 
          setShowAllSites(false); 
          setCurrentPage(1); 
      };
      window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
      window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      return () => {
          window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAllSites);
          window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMySite);
      };
  }, [isSuperAdmin]);

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] overflow-hidden font-bold uppercase tracking-tighter shadow-inner ${isRTL ? 'font-cairo' : ''}`} style={{ fontSize: `${fontSize}px` }} dir={isRTL ? 'rtl' : 'ltr'}>
      
      {/* 1. Header & Global Search */}
      <div className="bg-white p-3 border-b shadow-sm z-10 space-y-3">
        <div className={`flex justify-between items-center`}>
          <div className={`flex items-center gap-6 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <h2 className="text-[#005495] font-black italic uppercase text-lg flex items-center gap-2 tracking-tighter">
              <RefreshCw size={20} className={loading ? 'animate-spin text-blue-500' : 'cursor-pointer text-blue-900'} onClick={() => fetchLocations()} /> 
              <MapPin size={20} className="text-blue-600" />
              {t('locationList.title')}
            </h2>
            
            <div className="relative group">
              <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-2.5 text-gray-400 group-focus-within:text-blue-500 transition-colors`} size={14} />
              <input 
                className={`${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'} py-2 border border-gray-300 w-[400px] outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-50 shadow-inner rounded-sm font-black text-blue-900 placeholder:font-normal placeholder:italic`} 
                placeholder={t('locationList.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
              />
            </div>

            {(searchTerm || Object.values(filters).some(v => v !== "")) && (
              <button onClick={clearAllFilters} className="text-[10px] text-red-600 font-black uppercase hover:bg-red-50 px-3 py-1.5 rounded transition-all flex items-center gap-1 border border-red-100 ml-2">
                <X size={12}/> {t('locationList.btnClearAll')}
              </button>
            )}
          </div>
          
          <div className={`flex gap-3 ${isRTL ? 'ml-4' : 'mr-4'}`}>
            <button className="flex items-center gap-2 bg-white border border-gray-200 px-6 py-2.5 rounded-sm font-black text-gray-600 hover:bg-gray-50 uppercase tracking-tighter shadow-sm transition-all">
              <Download size={16} /> {t('locationList.btnExport')}
            </button>
            {canCreate && (
            <button 
              onClick={() => navigate('/locations/new')}
              className={`bg-[#005495] text-white px-8 py-2.5 rounded-sm font-black flex items-center gap-2 uppercase hover:bg-black transition-all shadow-lg active:scale-95`}
            >
              <Plus size={18} strokeWidth={3} /> {t('locationList.btnNewLocation')}
            </button>
            )}
          </div>
        </div>

        {/* 2. Advanced Organization/Site Filters */}
        {isSuperAdmin && (
          <div className={`flex items-center gap-4 ${isRTL ? 'mr-4' : 'ml-4'} bg-gray-50 p-2.5 rounded-sm border border-gray-200 w-fit shadow-sm`}>
            <div className={`flex items-center gap-2 border-gray-300 ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
              <Globe size={16} className="text-blue-600" />
              <span className="text-gray-400 italic">Org:</span>
              <select 
                className="bg-transparent outline-none font-black text-blue-900 uppercase cursor-pointer"
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
              >
                <option value="">{t('assets.list.all_orgs', 'ALL ORGS')}</option>
                {orgsList.map(org => (<option key={org.org_id} value={org.org_id}>{org.org_id}</option>))}
              </select>
            </div>

            <div className={`flex items-center gap-2 border-gray-300 ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
              <Building2 size={16} className="text-blue-600" />
              <span className="text-gray-400 italic">Site:</span>
              <select 
                className="bg-transparent outline-none font-black text-blue-900 uppercase cursor-pointer disabled:opacity-30"
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                disabled={!selectedOrg}
              >
                <option value="">{t('assets.list.all_sites', 'ALL SITES')}</option>
                {sitesList.map(site => (<option key={site.site_id} value={site.site_id}>{site.site_id}</option>))}
              </select>
            </div>

            {(selectedOrg || selectedSite) && (
              <button 
                onClick={() => {
                    setSelectedOrg(""); setSelectedSite(""); 
                    setCurrentPage(1);
                }}
                className="text-[10px] text-red-600 font-black uppercase hover:bg-red-50 px-3 py-1 rounded transition-all flex items-center gap-1 border border-red-100"
              >
                <X size={12}/> {t('common.cancel', 'CANCEL')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 3. Main Table Content */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar bg-[#f8f9fb]">
        <div className="bg-white border-2 border-gray-200 shadow-xl rounded-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#1a3353] text-white font-black uppercase sticky top-0 z-30 shadow-md">
              <tr className={isRTL ? 'flex-row-reverse' : ''}>
                <th className="p-3 border-r border-white/10 w-12 text-center">#</th>
                {[
                  { label: t('locationList.thLocationId'), key: "location_id", width: "" },
                  { label: t('locationList.thDescription'), key: "description", width: "" },
                  { label: t('locationList.thType'), key: "type", width: "w-32" },
                  { label: t('locationList.thStatus'), key: "status", width: "w-32 text-center" },
                  { label: t('locationList.thSite'), key: "site_id", width: "w-24 text-center" }
                ].map(col => (
                  <th key={col.key} className={`p-3 border-r border-white/10 ${col.width} cursor-pointer hover:bg-blue-800 transition-colors ${isRTL && !col.width.includes('text-center') ? 'text-right' : ''}`} onClick={() => requestSort(col.key)}>
                    <div className={`flex items-center gap-2 ${col.width.includes('text-center') ? 'justify-center' : 'justify-between'}`}>
                      {col.label}
                      <ArrowUpDown size={12} className={sortConfig.key === col.key ? "text-orange-400" : "text-white/30"} />
                    </div>
                  </th>
                ))}
              </tr>
              
              {/* Filters Row */}
              <tr className="bg-slate-100 shadow-inner">
                <td></td>
                <td className="p-1.5 border-r border-gray-300">
                  <input name="location_id" value={filters.location_id} onChange={handleFilterChange} className="w-full border border-gray-300 p-1 outline-none font-bold text-blue-900 uppercase" placeholder="..." />
                </td>
                <td className="p-1.5 border-r border-gray-300">
                  <input name="description" value={filters.description} onChange={handleFilterChange} className="w-full border border-gray-300 p-1 outline-none font-bold text-blue-900" placeholder="..." />
                </td>
                <td className="p-1.5 border-r border-gray-300">
                  <input name="type" value={filters.type} onChange={handleFilterChange} className="w-full border border-gray-300 p-1 outline-none font-bold text-blue-900 uppercase" placeholder="..." />
                </td>
                <td className="p-1.5 border-r border-gray-300">
                  <select name="status" value={filters.status} onChange={handleFilterChange} className="w-full border border-gray-300 p-1 outline-none font-bold text-blue-900 uppercase">
                      <option value="">{t('locationList.optAll')}</option>
                      <option value="OPERATING">{t('locationList.optOperating')}</option>
                      <option value="NOT OPERATING">NOT OPERATING</option>
                      <option value="DECOMMISSIONED">{t('locationList.optDecommissioned')}</option>
                  </select>
                </td>
                <td className="p-1.5 border-r border-gray-300">
                  <input name="site_id" value={filters.site_id} onChange={handleFilterChange} className="w-full border border-gray-300 p-1 outline-none font-bold text-blue-900 uppercase text-center" placeholder="..." />
                </td>
              </tr>
            </thead>
            
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="6" className="p-20 text-center italic text-blue-900 font-black animate-pulse text-sm uppercase tracking-widest bg-gray-50">{t('workflow.loading', 'LOADING...')}</td></tr>
              ) : locations.length === 0 ? (
                <tr><td colSpan="6" className="p-20 text-center italic text-gray-400 text-xs font-black uppercase bg-gray-50 border-dashed border-2 m-4">{t('dashboard.no_data', 'NO RECORDS FOUND')}</td></tr>
              ) : locations.map((loc, i) => (
                <tr 
                  key={loc.id || i} 
                  onClick={() => handleRowClick(loc.id)} 
                  className={`hover:bg-blue-50 cursor-pointer group transition-all ${isRTL ? 'text-right' : ''}`}
                >
                  <td className="p-3 border-r border-gray-100 text-center text-gray-400 font-mono">{(currentPage - 1) * itemsPerPage + i + 1}</td>
                  <td className="p-3 border-r border-gray-100 font-black text-blue-900 uppercase group-hover:text-blue-600 transition-colors">{loc.location_id}</td>
                  <td className="p-3 border-r border-gray-100 text-gray-700 font-bold">{loc.description}</td>
                  <td className="p-3 border-r border-gray-100 font-black text-gray-400 italic">{loc.type}</td>
                  <td className="p-3 border-r border-gray-100 text-center">
                    <span className={`px-3 py-1 rounded-sm text-[9px] font-black shadow-sm ring-1 ring-inset ${loc.status === 'OPERATING' ? 'bg-green-100 text-green-700 ring-green-600/20' : 'bg-gray-100 text-gray-600 ring-gray-500/20'}`}>
                      {loc.status}
                    </span>
                  </td>
                  <td className="p-3 border-r border-gray-100 font-black text-gray-700 text-center uppercase">{loc.site_id || '---'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Pagination Footer */}
      <div className={`bg-[#1a3353] text-white p-3 px-10 flex justify-between items-center shadow-2xl z-40 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-10 text-[10px] font-black uppercase tracking-widest ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex items-center gap-2">
            <span className="text-blue-400 italic">{t('locationList.recordsFound', 'TOTAL RECORDS')}</span>
            <span className="bg-blue-900 px-3 py-1 rounded-sm shadow-inner border border-white/10 text-orange-400">{totalRecords}</span>
          </div>
          <div className={`flex items-center gap-2 border-white/10 ${isRTL ? 'border-r pr-10' : 'border-l pl-10'}`}>
            <span className="text-blue-400 italic">{t('locationList.page', 'PAGE')}</span>
            <span className="text-white">{currentPage}</span>
            <span className="text-white/30 mx-1">/</span>
            <span className="text-white">{totalPages || 1}</span>
          </div>
        </div>
        
        <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <button 
            disabled={currentPage === 1} 
            onClick={() => setCurrentPage(p => p - 1)} 
            className="p-2 border border-white/20 rounded-sm disabled:opacity-10 hover:bg-orange-500 hover:border-orange-500 transition-all shadow-md active:scale-90"
          >
            <ChevronLeft size={20} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <button 
            disabled={currentPage === totalPages || totalPages === 0} 
            onClick={() => setCurrentPage(p => p + 1)} 
            className="p-2 border border-white/20 rounded-sm disabled:opacity-10 hover:bg-orange-500 hover:border-orange-500 transition-all shadow-md active:scale-90"
          >
            <ChevronRight size={20} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationList;